import React from 'react'
import {
  Box,
  HStack,
  Text,
  Badge,
  IconButton,
  SimpleGrid,
  Table,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import type { UnionBusinessAffiliation, UnionWorksiteAffiliation, Steward } from '../../types/union'
import { formatContractPeriod } from '../shared/InfoField'
import { StewardBadge } from './StewardBadge'
import { StatusMenu } from './StatusMenu'
import { WorksiteStatusMenu } from './WorksiteStatusMenu'
import { InlineEditTextarea } from '../shared/InlineEditTextarea'
import { InlineContactSelect } from '../shared/InlineContactSelect'
import { ContactSearchSelect } from '../shared/ContactSearchSelect'
import type { ContractEditTarget } from './EditContractModal'

export function UnionBusinessesSection({
  filteredBusinesses,
  expandedBusinessIds,
  toggleBusinessExpand,
  handleBusinessStatusChange,
  handleBusinessFieldSave,
  handleBusinessNotesSave,
  handleWorksiteStatusChange,
  handleWorksiteRepSave,
  handleWorksiteNotesSave,
  handleRemoveSteward,
  setContractEditTarget,
  setDeleteTarget,
  addingStewardFor,
  setAddingStewardFor,
  handleAddSteward,
}: {
  filteredBusinesses: UnionBusinessAffiliation[]
  expandedBusinessIds: Set<string>
  toggleBusinessExpand: (id: string) => void
  handleBusinessStatusChange: (ab: UnionBusinessAffiliation, isActive: boolean) => void
  handleBusinessFieldSave: (ab: UnionBusinessAffiliation, fieldName: string, value: string | null, name?: string | null) => void
  handleBusinessNotesSave: (businessId: string, fieldName: string, value: string) => void
  handleWorksiteStatusChange: (ab: UnionBusinessAffiliation, wo: UnionWorksiteAffiliation, newStatus: 'covered' | 'local' | 'excluded') => void
  handleWorksiteRepSave: (ab: UnionBusinessAffiliation, wo: UnionWorksiteAffiliation, repName: string | null) => void
  handleWorksiteNotesSave: (worksiteId: string, fieldName: string, value: string) => void
  handleRemoveSteward: (worksiteId: string, stewardId: string) => void
  setContractEditTarget: (target: ContractEditTarget | null) => void
  setDeleteTarget: (target: { type: 'business' | 'standalone'; id: string; name: string } | null) => void
  addingStewardFor: string | null
  setAddingStewardFor: (worksiteId: string | null) => void
  handleAddSteward: (worksiteId: string, contactId: string | null, name?: string | null, role?: string) => void
}) {
  const navigate = useNavigate()
  return (
    <>
      {filteredBusinesses.map((ab: UnionBusinessAffiliation) => {
        const isExpanded = expandedBusinessIds.has(ab.businessUnionId)
        return (
          <React.Fragment key={ab.businessUnionId}>
            <Table.Row
              _hover={{ bg: 'gray.50', cursor: 'pointer' }}
              onClick={() => toggleBusinessExpand(ab.businessUnionId)}
            >
              <Table.Cell>
                <HStack gap={1}>
                  <Box as={isExpanded ? FiChevronDown : FiChevronRight} boxSize={3} color="gray.400" />
                  <Badge colorPalette="purple" fontSize="xs">Business</Badge>
                </HStack>
              </Table.Cell>
              <Table.Cell>
                <Text fontWeight="medium" fontSize="sm">{ab.businessName}</Text>
                <Text fontSize="xs" color="gray.500">
                  {ab.coveredWorksiteCount} worksite{ab.coveredWorksiteCount !== 1 ? 's' : ''}
                  {ab.overrideCount > 0 && `, ${ab.overrideCount} local`}
                  {ab.excludedWorksiteCount > 0 && `, ${ab.excludedWorksiteCount} excluded`}
                </Text>
              </Table.Cell>
              <Table.Cell><Text fontSize="sm">{ab.businessType || '---'}</Text></Table.Cell>
              <Table.Cell>
                <HStack gap={1}>
                  <Text fontSize="sm">{formatContractPeriod(ab.contractStartDate, ab.contractEndDate)}</Text>
                  <IconButton
                    aria-label="Edit contract period"
                    size="xs"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation()
                      setContractEditTarget({
                        type: 'business',
                        label: ab.businessName,
                        startDate: ab.contractStartDate,
                        endDate: ab.contractEndDate,
                        businessId: ab.businessId,
                      })
                    }}><FiEdit2 /></IconButton>
                </HStack>
              </Table.Cell>
              <Table.Cell>
                <StatusMenu
                  isActive={ab.isActive}
                  onStatusChange={(active) => handleBusinessStatusChange(ab, active)}
                />
              </Table.Cell>
              <Table.Cell>
                <IconButton
                  aria-label="Delete affiliation"
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'business', id: ab.businessId, name: ab.businessName }) }}><FiTrash2 /></IconButton>
              </Table.Cell>
            </Table.Row>

            {/* Expanded detail panel */}
            {isExpanded && (
              <Table.Row key={`${ab.businessUnionId}-detail`}>
                <Table.Cell colSpan={6} p={0} borderBottom="2px solid" borderColor="purple.100">
                  <Box bg="purple.50" px={6} py={4}>
                    {/* Editable contact fields */}
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={3} mb={4}>
                      <Box bg="gray.50" p={3} borderRadius="md" minH="60px">
                        <InlineEditTextarea
                          label="Union Rep"
                          value={ab.unionRepName}
                          fieldName="unionRepName"
                          canEdit={true}
                          onSave={(_, value) => handleBusinessFieldSave(ab, 'unionRepName', value)}
                          rows={1}
                        />
                      </Box>
                      <Box bg="gray.50" p={3} borderRadius="md" minH="60px">
                        <InlineContactSelect
                          label="Employer Contact"
                          contactId={ab.employerContactId || undefined}
                          contactName={ab.employerContactName || undefined}
                          fieldName="employerContactId"
                          canEdit={true}
                          onSave={(_, contactId, name) => handleBusinessFieldSave(ab, 'employerContactId', contactId, name)}
                          allowFreeText
                        />
                        {ab.employerContactId && (
                          <Text
                            fontSize="xs" color="accent" cursor="pointer" mt={1}
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => navigate(`/dashboard/contacts/${ab.employerContactId}`)}
                          >
                            View contact →
                          </Text>
                        )}
                      </Box>
                      <Box bg="gray.50" p={3} borderRadius="md" minH="60px">
                        <InlineEditTextarea
                          label="Notes"
                          value={ab.notes}
                          fieldName="notes"
                          canEdit={true}
                          onSave={(_, value) => handleBusinessNotesSave(ab.businessId, 'notes', value)}
                          rows={2}
                        />
                      </Box>
                    </SimpleGrid>

                    {/* Covered worksites sub-table */}
                    <Text fontSize="sm" fontWeight="semibold" mb={2} color="purple.700">
                      Covered Worksites ({ab.coveredWorksites.length})
                    </Text>
                    <Table.Root size="sm" bg="surface.2" borderRadius="md" overflow="hidden">
                      <Table.Header bg="gray.50">
                        <Table.Row>
                          <Table.ColumnHeader>Status</Table.ColumnHeader>
                          <Table.ColumnHeader>Site</Table.ColumnHeader>
                          <Table.ColumnHeader>Steward(s)</Table.ColumnHeader>
                          <Table.ColumnHeader>Rep</Table.ColumnHeader>
                          <Table.ColumnHeader>Contract</Table.ColumnHeader>
                          <Table.ColumnHeader>Notes</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {ab.coveredWorksites.map((wo: UnionWorksiteAffiliation) => (
                          <Table.Row
                            key={wo.worksiteId}
                          >
                            <Table.Cell>
                              <WorksiteStatusMenu
                                wo={wo}
                                onStatusChange={(newStatus) => handleWorksiteStatusChange(ab, wo, newStatus)}
                              />
                            </Table.Cell>
                            <Table.Cell opacity={wo.isExcluded ? 0.5 : 1}>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                textDecoration={wo.isExcluded ? 'line-through' : undefined}
                                color="accent"
                                cursor="pointer"
                                _hover={{ textDecoration: 'underline' }}
                                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/worksites/${wo.worksiteId}`) }}
                              >
                                {wo.worksiteName}
                              </Text>
                              {wo.city && <Text fontSize="xs" color="gray.500">{wo.city}</Text>}
                            </Table.Cell>
                            <Table.Cell opacity={wo.isExcluded ? 0.5 : 1}>
                              <HStack gap={1} flexWrap="wrap">
                                {wo.stewards && wo.stewards.map((s: Steward) => (
                                  <StewardBadge
                                    key={s.worksiteStewardId}
                                    steward={s}

                                    onRemove={() => handleRemoveSteward(wo.worksiteId, s.worksiteStewardId)}
                                  />
                                ))}
                                {addingStewardFor === wo.worksiteId ? (
                                  <Box w="180px">
                                    <ContactSearchSelect
                                      onSelect={(contactId, name) => {
                                        if (contactId || name) handleAddSteward(wo.worksiteId, contactId, name)
                                        else setAddingStewardFor(null)
                                      }}
                                      placeholder="Search steward..."
                                      size="xs"
                                      allowFreeText
                                    />
                                  </Box>
                                ) : (
                                  <IconButton
                                    aria-label="Add steward"
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="teal"
                                    onClick={(e) => { e.stopPropagation(); setAddingStewardFor(wo.worksiteId) }}><FiPlus /></IconButton>
                                )}
                              </HStack>
                            </Table.Cell>
                            <Table.Cell opacity={wo.isExcluded ? 0.5 : 1}>
                              {wo.isExcluded ? (
                                <Text fontSize="xs" color="gray.400">---</Text>
                              ) : (
                                <Box w="150px">
                                  <InlineEditTextarea
                                    label=""
                                    value={wo.unionRepName}
                                    fieldName="unionRepName"
                                    canEdit={true}
                                    onSave={(_, value) => handleWorksiteRepSave(ab, wo, value)}
                                    rows={1}
                                  />
                                </Box>
                              )}
                            </Table.Cell>
                            <Table.Cell opacity={wo.isExcluded ? 0.5 : 1}>
                              <HStack gap={1}>
                                <Text
                                  fontSize="sm"
                                  color={!wo.isOverride && !wo.isExcluded ? 'gray.500' : undefined}
                                  fontStyle={!wo.isOverride && !wo.isExcluded ? 'italic' : undefined}
                                >
                                  {formatContractPeriod(wo.contractStartDate, wo.contractEndDate)}
                                </Text>
                                {!wo.isExcluded && (
                                  <IconButton
                                    aria-label="Edit contract period"
                                    size="xs"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setContractEditTarget({
                                        type: 'worksite-override',
                                        label: wo.worksiteName,
                                        startDate: wo.contractStartDate,
                                        endDate: wo.contractEndDate,
                                        businessId: ab.businessId,
                                        worksiteId: wo.worksiteId,
                                        isOverride: wo.isOverride,
                                        isExcluded: wo.isExcluded,
                                        unionRepName: wo.unionRepName,
                                        employerContactId: wo.employerContactId,
                                        employerContactName: wo.employerContactName,
                                      })
                                    }}><FiEdit2 /></IconButton>
                                )}
                              </HStack>
                            </Table.Cell>
                            <Table.Cell opacity={wo.isExcluded ? 0.5 : 1}>
                              <Box w="150px">
                                <InlineEditTextarea
                                  label=""
                                  value={wo.notes}
                                  fieldName="notes"
                                  canEdit={true}
                                  onSave={(_, value) => handleWorksiteNotesSave(wo.worksiteId, 'notes', value)}
                                  rows={2}
                                />
                              </Box>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                </Table.Cell>
              </Table.Row>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
